import {isUndefined} from 'lodash';
import React, {FC, RefObject, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {AsyncHandlerOf, Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {buildContactsManagerSyncsUrl} from '../../helpers/contactsManagerHelpers';
import {
  CardAttributeUpdateAction,
  cardAttributeUpdateReducer,
} from '../../helpers/models/cardAttributeUpdateHelpers';
import {
  ConflictingMergeCardValues,
  handleSettingOverrideMergeValues,
} from '../../helpers/models/cardMergeHelpers';
import {globalNamespace} from '../../helpers/models/namespaceHelpers';
import {CardModel} from '../../models/api/cardModel';
import {ConversationModel} from '../../models/api/conversationModel';
import {CrmSyncIntegrationResourceTypesEnum} from '../../models/api/crmSyncModel';
import {ContactDetailSectionsExpandState} from '../../models/api/localPreferencesModel';
import {PreviewCardMergeModel} from '../../models/api/previewCardMergeModel';
import {VisualContactModel} from '../../models/api/visualContactModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {OverrideMergeValues} from '../../store/data/cards/cardsActions';
import {ContactDetailsErrorTypesEnum} from '../../store/panels/app/banners/bannersState';
import {
  ContactDetailsPanelLocationsEnum,
  ContactDetailsPanelState,
} from '../../store/panels/contacts/contactDetailsPanelState';
import {
  ContactDetailsPanelSubState,
  subStateIsAccountDetailsSubState,
  subStateIsCreateAccountSubState,
  subStateIsMergeSubState,
} from '../../store/panels/contacts/contactDetailsPanelSubState';
import {ContactDetailsPanelId} from '../../store/panels/panelStateBase';
import {CreateAccountFooterContainer} from '../accounts/createAccount/createAccountFooterContainer';
import {CreateAccountFormContainer} from '../accounts/createAccount/createAccountFormContainer';
import {Accordion} from '../common/accordion/accordion';
import {CrmLastSyncedBannerContainer} from '../common/crm/crmLastSyncedBannerContainer';
import {useLinkNavigation} from '../common/navigation/useLinkNavigation';
import {ContactDetailsAccountDetailsWrapperContainer} from './contactDetailsAccountDetailsWrapperContainer';
import {
  ContactDetailsContext,
  ContactDetailsContextProps,
  ContactDetailsMergeContextProps,
  ContactDetailsStoredActionsContextProps,
  ContactDetailsVersionsEnum,
  CreateCardAsyncHandler,
  SetMergeOverridesOptions,
} from './contactDetailsContext';
import {ContactDetailsEmptyState} from './contactDetailsEmptyState';
import {
  buildContactDetailsViewEvent,
  buildContactMergePayload,
  computeAssignedCardType,
  ContactDetailsTrackEventHandler,
} from './contactDetailsTrackingHelpers';
import {ContactHeader} from './contactHeader/contactHeader';
import {ContactMergeFooter} from './contactMerge/contactMergeFooter';
import {ContactNotesHandle} from './contactNotes/contactNotes';
import {
  buildAccordionSections,
  OnContactDetailsSectionToggledHandler,
  renderContactDetailsLoadingSections,
  renderContactDetailsMergePreview,
} from './helpers/accordionSectionHelpers';
import {ContactPluginHeaderContainer} from './pluginHeader/contactPluginHeaderContainer';

/*
 * Types.
 */

interface ContactDetailsProps {
  panelId: ContactDetailsPanelId;
  conversation: ConversationModel | undefined;
  contactDetailsPanel: ContactDetailsPanelState;
  selectedVisualContact: VisualContactModel | undefined;
  selectedCard: CardModel | undefined;
  duplicateCard: CardModel | undefined;
  contacts: ReadonlyArray<VisualContactModel>;
  sectionExpandState: ContactDetailSectionsExpandState;
  conflictingMergeValues?: ConflictingMergeCardValues;
  onTrackEvent?: ContactDetailsTrackEventHandler;
  onContactDetailsSectionToggled: OnContactDetailsSectionToggledHandler;
  previewMerge: (
    panelId: ContactDetailsPanelId,
    mainCardId: number,
    mergeCardIds: Array<number>,
  ) => Promise<void>;
  mergeAndFetchContactDetailCards: (
    panelId: ContactDetailsPanelId,
    mainCardId: number,
    mergeCardIds: readonly number[],
    shouldRequireRefetch?: boolean,
    overrides?: OverrideMergeValues,
  ) => Promise<void>;
  clearMerge: AsyncHandlerOf<ContactDetailsPanelId>;
  showContactDetailsMergeErrorBanner: (
    panelId: ContactDetailsPanelId,
    type: ContactDetailsErrorTypesEnum,
    card?: CardModel,
  ) => void;
  updateContactDetailsSelectedDuplicateCard: (
    panelId: ContactDetailsPanelId,
    cardId: number,
  ) => Promise<void>;
  onClose?: Handler;
  addCardToAccount: (accountId: number, card: CardModel) => void;
  createNewCardAsync: CreateCardAsyncHandler;
  onDelete?: HandlerOf<CardModel>;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
`;

const StyledScrollableContentDiv = styled.div`
  overflow: auto;
`;

const StyledAccordion = styled(Accordion)`
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledCrmLastSyncedBannerContainer = styled(CrmLastSyncedBannerContainer)`
  margin: 4px 4px 0 4px;
`;

/*
 * Component.
 */

export const ContactDetails: FC<ContactDetailsProps> = (props) => {
  const {
    panelId,
    conversation,
    contactDetailsPanel,
    selectedVisualContact,
    selectedCard,
    duplicateCard,
    contacts,
    conflictingMergeValues,
    onTrackEvent,
    previewMerge,
    mergeAndFetchContactDetailCards,
    clearMerge,
    showContactDetailsMergeErrorBanner,
    updateContactDetailsSelectedDuplicateCard,
    onClose,
    onDelete,
    addCardToAccount,
    createNewCardAsync,
  } = props;
  const {previewCardMerge, mergeMainCardId, mergeCardIds, mergeStatus} = getMergeDetailsFromSubState(
    contactDetailsPanel.subState,
  );
  const isMerging = Boolean(mergeStatus);
  const isPopup = Boolean(contactDetailsPanel.popupAppPanelId);

  const notesRef = useRef<ContactNotesHandle>(null);
  const [unsavedCard, setUnsavedCard] = useState<CardModel>();
  const {navigateToLink} = useLinkNavigation();

  // Effect for tracking. Use primitives as dependencies to avoid extra tracking.
  const selectedContactUid = selectedVisualContact?.contact.uid;
  const namespace = selectedCard?.namespace || selectedVisualContact?.namespace || globalNamespace;
  const selectedCardId = selectedCard?.id;
  const hasMultipleContacts = contacts.length > 1;
  const assignedCardType = computeAssignedCardType(selectedCard);
  const hasDuplicateCard = Boolean(duplicateCard);
  useEffect(
    () => {
      if (selectedContactUid && onTrackEvent && contactDetailsPanel.status === AsyncStatusesEnum.LOADED) {
        onTrackEvent(
          buildContactDetailsViewEvent(
            namespace,
            selectedCardId,
            selectedContactUid,
            hasMultipleContacts,
            assignedCardType,
            hasDuplicateCard,
            isPopup,
            Boolean(selectedCard?.accountId),
          ),
        );
      }
    },
    // We only want to track when the selected contact changes or the selected card changes and the data has been loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedContactUid, contactDetailsPanel.status, selectedCardId],
  );

  // Set up state and context
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [overrideMergeValues, setOverrideMergeValues] = useState<OverrideMergeValues | undefined>(undefined);
  const [storedActions, setStoredActions] = useState<ReadonlyArray<CardAttributeUpdateAction>>([]);
  const contactDetailsMergeContext = setupContactDetailsMergeContext(
    panelId,
    selectedCard,
    isMerging,
    mergeMainCardId,
    mergeCardIds,
    conflictingMergeValues,
    overrideMergeValues,
    setIsReadOnly,
    previewMerge,
    mergeAndFetchContactDetailCards,
    clearMerge,
    showContactDetailsMergeErrorBanner,
    setOverrideMergeValues,
  );
  const contactDetailsStoredActionsContext = setupContactDetailsStoredActionsContext(
    storedActions,
    unsavedCard,
    setStoredActions,
    setUnsavedCard,
  );
  const contactDetailsContext = setupContactDetailsContext(
    panelId,
    isReadOnly,
    conversation?.namespace,
    contactDetailsMergeContext,
    contactDetailsStoredActionsContext,
    isPopup,
    Boolean(contactDetailsPanel.shouldHideHeaderTabs),
    onTrackEvent,
    updateContactDetailsSelectedDuplicateCard,
    createNewCardAsync,
  );

  useEffect(() => {
    // If we are no longer merging, but we have a preview card still in the state. Clear the merge information.
    if (!isMerging && previewCardMerge) {
      setIsReadOnly(false);
      clearMerge(panelId);
    }
  }, [isMerging, previewCardMerge, clearMerge, panelId]);

  const isLoading =
    isUndefined(contactDetailsPanel.status) ||
    contactDetailsPanel.status === AsyncStatusesEnum.LOADING ||
    (isMerging && !previewCardMerge);
  return (
    <ContactDetailsContext.Provider value={contactDetailsContext}>
      <StyledWrapperDiv>
        <ContactPluginHeaderContainer
          panelId={panelId}
          location={contactDetailsPanel.location}
          selectedVisualContact={selectedVisualContact}
          selectedCard={selectedCard}
          contacts={contacts}
          conversation={conversation}
          isLoading={isLoading}
          onClose={onClose}
          onDelete={(cardId) => {
            if (onDelete) {
              onDelete(cardId);
            }
          }}
        />
        <StyledScrollableContentDiv>
          {maybeRenderContactDetails(props, isLoading, isMerging, notesRef, navigateToLink, previewCardMerge)}
          {maybeRenderAccountDetails(props, isLoading)}
          {maybeRenderCreateAccount(props, isLoading)}
          {maybeRenderEmptyState(isLoading, Boolean(selectedVisualContact), contactDetailsPanel.location)}
        </StyledScrollableContentDiv>
        {maybeRenderFooter(
          isLoading,
          panelId,
          previewCardMerge,
          contactDetailsPanel.subState,
          selectedCard,
          addCardToAccount,
        )}
      </StyledWrapperDiv>
    </ContactDetailsContext.Provider>
  );
};

/*
 * Render Helpers.
 */

const maybeRenderFooter = (
  isLoading: boolean,
  panelId: ContactDetailsPanelId,
  previewCardMerge: PreviewCardMergeModel | undefined,
  subState: ContactDetailsPanelSubState | undefined,
  card: CardModel | undefined,
  addCardToAccount: (accountId: number, card: CardModel) => void,
) => {
  if (isLoading) {
    return null;
  }
  if (previewCardMerge) {
    return <ContactMergeFooter />;
  }
  if (subState && subStateIsCreateAccountSubState(subState)) {
    return (
      <CreateAccountFooterContainer
        panelId={panelId}
        createAccountFormState={subState}
        onAccountCreated={(accountId) => {
          if (card) {
            addCardToAccount(accountId, card);
          }
        }}
      />
    );
  }
  return null;
};

const maybeRenderContactDetails = (
  props: ContactDetailsProps,
  isLoading: boolean,
  isMerging: boolean,
  notesRef: RefObject<ContactNotesHandle>,
  navigateToLink: HandlerOf<string>,
  previewCardMerge?: PreviewCardMergeModel,
) => {
  const {
    panelId,
    contactDetailsPanel,
    sectionExpandState,
    selectedCard,
    selectedVisualContact,
    onContactDetailsSectionToggled,
  } = props;
  const {subState, location} = contactDetailsPanel;

  if (subState && (subStateIsAccountDetailsSubState(subState) || subStateIsCreateAccountSubState(subState))) {
    return null;
  }
  if (isLoading) {
    return renderContactDetailsLoadingSections(isMerging, sectionExpandState);
  }
  if (!selectedCard) {
    return renderNoSelectedCardSections(props, notesRef);
  }
  if (previewCardMerge) {
    return renderMergePreviewSections(props, previewCardMerge);
  }
  // We only will show no contact when in inboxes.
  if (!selectedVisualContact && location === ContactDetailsPanelLocationsEnum.INBOXES) {
    return null;
  }
  return (
    <>
      {maybeRenderCrmSyncBanner(selectedCard, navigateToLink)}
      <ContactHeader contact={selectedVisualContact} card={selectedCard} />
      <StyledAccordion
        key="standard-accordion"
        allowMultiSelect
        sections={buildAccordionSections({
          expandState: sectionExpandState,
          card: selectedCard,
          contact: selectedVisualContact,
          panelId,
          notesRef,
          onContactDetailsSectionToggled,
        })}
      />
    </>
  );
};

function maybeRenderCrmSyncBanner(card: CardModel, navigateToLink: HandlerOf<string>) {
  const {externalInfo} = card;
  if (!externalInfo) {
    return null;
  }
  return (
    <StyledCrmLastSyncedBannerContainer
      resourceType={CrmSyncIntegrationResourceTypesEnum.CARD}
      source={externalInfo.source}
      onManageSyncClick={() => navigateToLink(buildContactsManagerSyncsUrl(externalInfo.source))}
      shouldRenderTooltip
    />
  );
}

const maybeRenderAccountDetails = (
  {panelId, selectedCard, selectedVisualContact, contactDetailsPanel: {subState}}: ContactDetailsProps,
  isLoading: boolean,
) => {
  if (!subState || !subStateIsAccountDetailsSubState(subState)) {
    return null;
  }
  return (
    <ContactDetailsAccountDetailsWrapperContainer
      panelId={panelId}
      card={selectedCard}
      contact={selectedVisualContact}
      subState={subState}
      isLoading={isLoading}
    />
  );
};

const maybeRenderCreateAccount = (
  {panelId, contactDetailsPanel: {subState}}: ContactDetailsProps,
  isLoading: boolean,
) => {
  if (!subState || !subStateIsCreateAccountSubState(subState) || isLoading) {
    return null;
  }
  return <CreateAccountFormContainer panelId={panelId} createAccountFormState={subState} />;
};

function maybeRenderEmptyState(
  isLoading: boolean,
  hasVisualContact: boolean,
  location: ContactDetailsPanelLocationsEnum,
) {
  if (isLoading || hasVisualContact || location !== ContactDetailsPanelLocationsEnum.INBOXES) {
    return null;
  }
  return <ContactDetailsEmptyState />;
}

function renderNoSelectedCardSections(
  {panelId, selectedVisualContact, sectionExpandState, onContactDetailsSectionToggled}: ContactDetailsProps,
  notesRef: RefObject<ContactNotesHandle>,
) {
  if (!selectedVisualContact) {
    return null;
  }
  return (
    <>
      <ContactHeader contact={selectedVisualContact} />
      <StyledAccordion
        key="preview-accordion"
        allowMultiSelect
        sections={buildAccordionSections({
          expandState: sectionExpandState,
          contact: selectedVisualContact,
          panelId,
          notesRef,
          onContactDetailsSectionToggled,
        })}
      />
    </>
  );
}

const renderMergePreviewSections = (
  {panelId, contactDetailsPanel, selectedVisualContact}: ContactDetailsProps,
  previewCardMerge: PreviewCardMergeModel,
) => {
  if (!selectedVisualContact) {
    return null;
  }

  return renderContactDetailsMergePreview({
    panelId,
    contact: selectedVisualContact,
    previewCardMerge,
    noteIds: contactDetailsPanel.noteIds,
  });
};

/*
 * Helpers.
 */

function setupContactDetailsContext(
  panelId: ContactDetailsPanelId,
  isReadOnly: boolean,
  conversationNamespace: string | undefined,
  mergeContext: ContactDetailsMergeContextProps,
  storedActionsContext: ContactDetailsStoredActionsContextProps,
  isPopup: boolean,
  shouldHideHeaderTabs: boolean,
  onTrackEvent: ContactDetailsTrackEventHandler | undefined,
  updateContactDetailsSelectedDuplicateCard: (
    panelId: ContactDetailsPanelId,
    cardId: number,
  ) => Promise<void>,
  createNewCardAsync: CreateCardAsyncHandler,
): ContactDetailsContextProps {
  return {
    version: ContactDetailsVersionsEnum.VERSION_2,
    panelId,
    isReadOnly,
    isPopup,
    shouldHideHeaderTabs,
    conversationNamespace,
    onTrackEvent,
    updateSelectedCard: async (cardId: number) => {
      await updateContactDetailsSelectedDuplicateCard(panelId, cardId);
    },
    mergeContext,
    storedActionsContext,
    createNewCardAsync,
  };
}

function setupContactDetailsMergeContext(
  panelId: ContactDetailsPanelId,
  card: CardModel | undefined,
  isMerging: boolean,
  mainCardId: number | undefined,
  mergeCardIds: ReadonlyArray<number>,
  conflictingMergeValues: ConflictingMergeCardValues | undefined,
  overrideMergeValues: OverrideMergeValues | undefined,
  setIsReadOnly: React.Dispatch<React.SetStateAction<boolean>>,
  previewMerge: (
    panelId: ContactDetailsPanelId,
    mainCardId: number,
    mergeCardIds: Array<number>,
  ) => Promise<void>,
  mergeAndFetchContactDetailCards: (
    panelId: ContactDetailsPanelId,
    mainCardId: number,
    mergeCardIds: readonly number[],
    shouldRequireRefetch?: boolean,
    overrides?: OverrideMergeValues,
  ) => Promise<void>,
  clearMerge: AsyncHandlerOf<ContactDetailsPanelId>,
  showContactDetailsMergeErrorBanner: (
    panelId: ContactDetailsPanelId,
    type: ContactDetailsErrorTypesEnum,
    card?: CardModel,
  ) => void,
  setOverrideMergeValues: React.Dispatch<React.SetStateAction<OverrideMergeValues | undefined>>,
): ContactDetailsMergeContextProps {
  const updateMergeState = (isMergingCards: boolean) => {
    setIsReadOnly(isMergingCards);
    if (!isMergingCards) {
      clearMerge(panelId);
    }
  };
  return {
    isMerging,
    conflictingMergeValues,
    overrideMergeValues,
    startMerge: async (mainCId: number, cardIds: ReadonlyArray<number>) => {
      updateMergeState(true);
      try {
        await previewMerge(panelId, mainCId, [mainCId, ...cardIds]);
      } catch (err) {
        showContactDetailsMergeErrorBanner(panelId, ContactDetailsErrorTypesEnum.MERGE_PREVIEW_FAILED, card);
        updateMergeState(false);
      }
    },
    cancelMerge: () => {
      updateMergeState(false);
    },
    confirmMerge: async () => {
      if (card && mainCardId) {
        try {
          await mergeAndFetchContactDetailCards(
            panelId,
            mainCardId,
            mergeCardIds,
            card.id !== mainCardId,
            overrideMergeValues,
          );
          updateMergeState(false);
          trackInterfaceEvent(
            InterfaceEventsEnum.P4_CONTACT_DETAILS_MERGE,
            buildContactMergePayload(
              card.namespace,
              mainCardId,
              mergeCardIds.filter((cId) => cId !== mainCardId)[0],
            ),
          );
        } catch {
          showContactDetailsMergeErrorBanner(panelId, ContactDetailsErrorTypesEnum.MERGE_FAILED, card);
        }
      }
    },
    setMergeOverrides: (options: SetMergeOverridesOptions) => {
      handleSettingOverrideMergeValues(overrideMergeValues, options, setOverrideMergeValues);
    },
  };
}

function setupContactDetailsStoredActionsContext(
  storedActions: ReadonlyArray<CardAttributeUpdateAction>,
  unsavedCard: CardModel | undefined,
  setStoredActions: HandlerOf<ReadonlyArray<CardAttributeUpdateAction>>,
  setUnsavedCard: HandlerOf<CardModel | undefined>,
): ContactDetailsStoredActionsContextProps {
  return {
    storedActions,
    unsavedCard,
    onUpdateUnsavedCard: (action: CardAttributeUpdateAction) => {
      setStoredActions([...storedActions, action]);
      if (!unsavedCard) {
        return;
      }
      const nextCard = cardAttributeUpdateReducer(unsavedCard, action);
      setUnsavedCard(nextCard);
    },
    clearStoredActions: () => {
      setStoredActions([]);
    },
    onSetUnsavedCard: (card: CardModel | undefined) => {
      setUnsavedCard(card);
    },
  };
}

function getMergeDetailsFromSubState(subState?: ContactDetailsPanelSubState) {
  if (!subState || !subStateIsMergeSubState(subState)) {
    return {
      previewCardMerge: undefined,
      mergeMainCardId: undefined,
      mergeStatus: undefined,
      mergeCardIds: [],
    };
  }
  return subState;
}
